import { z } from 'zod';

/**
 * A tenant composing its own role from the fixed permission catalog —
 * "Tenants compose custom roles from this catalog but may not invent new
 * permissions" (decision D6, `permission-catalog.ts`). Every key is
 * checked server-side against that catalog and against the `platform`
 * module exclusion; this schema only bounds shape and size.
 */
export const createRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(200).nullish(),
  permissionKeys: z.array(z.string()).min(1).max(200),
  /** Pre-populate `permissionKeys` from an existing role (system or this
   *  tenant's own) — the "cloneable" half of `Role.isSystem`'s comment.
   *  Purely a client-side convenience; the server never reads this back,
   *  it only ever sees the resulting `permissionKeys`. */
  cloneFromRoleId: z.uuid().nullish(),
});
export type CreateRoleRequest = z.infer<typeof createRoleRequestSchema>;

/** Same shape as create — a custom role's permission set is replaced
 *  wholesale, never patched key by key, since a picker always submits the
 *  complete set it currently shows checked. */
export const updateRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(200).nullish(),
  permissionKeys: z.array(z.string()).min(1).max(200),
});
export type UpdateRoleRequest = z.infer<typeof updateRoleRequestSchema>;

export interface RoleDetailResponse {
  readonly id: string;
  readonly key: string;
  readonly nameKey: string;
  readonly name: string | null;
  readonly description: string | null;
  readonly isSystem: boolean;
  readonly permissions: readonly string[];
  /** How many active memberships currently hold this role — archiving is
   *  refused while this is above zero. */
  readonly activeAssignmentCount: number;
  readonly archivedAt: string | null;
  readonly createdAt: string;
}
